// BatchJobListTable.jsx
// Table component that lists batch jobs.
// Each row shows fields from BatchJobResponse and can be expanded to reveal pod details.
import { Fragment, useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";

import {
  ACCENT,
  FONT,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  SURFACE_MUTED,
  HAIRLINE,
} from "../../../../theme/tokens";

import PodDetailsTable from "../components/PodDetailsTable";
import StatusPill from "../components/StatusPill";
import { WorkerResourceChips } from "../components/ResourceChips";
import {
  dataTableHeadSx,
  SortableHeadCell,
} from "../../../../components/DataTableHeader.jsx";
import { formatTimestamp, formatDuration } from "../components/ViewUtils";

export const TERMINAL_STATUSES = [
  "Succeeded",
  "Failed",
  "Cancelled",
  "Timeout",
];
export const QUEUED_STATUSES = ["Queued", "Admitted"];

// Maps BatchJobStatus enum values to getStatusStyle color keys
const JOB_STATUS_COLOR = {
  Queued: "default",
  Admitted: "warning",
  Running: "success",
  Succeeded: "info",
  Timeout: "warning",
  Failed: "error",
  Cancelled: "default",
};

const JobStatusWord = (status) => {
  return status || "Unknown";
};
JobStatusWord.propTypes = {
  status: PropTypes.string,
};

const JobStatusChip = ({ status }) => {
  const color = JOB_STATUS_COLOR[status] || "default";
  const label = JobStatusWord(status);
  return <StatusPill label={label} color={color} />;
};
JobStatusChip.propTypes = {
  status: PropTypes.string,
};

const ProgressCell = ({ job }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (job.status !== "Running") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [job.status]);

  const isTerminal = TERMINAL_STATUSES.includes(job.status);
  const isQueued = QUEUED_STATUSES.includes(job.status);
  const isRunning = job.status === "Running";

  if (isTerminal && job.finished_at) {
    const startMs = job.started_at
      ? new Date(job.started_at).getTime()
      : job.submitted_at
        ? new Date(job.submitted_at).getTime()
        : null;
    const durationSecs =
      startMs != null
        ? Math.floor((new Date(job.finished_at).getTime() - startMs) / 1000)
        : null;
    return (
      <Box>
        <Typography variant="caption" color="textSecondary" display="block">
          Finished
        </Typography>
        <Typography variant="body2">
          {formatTimestamp(job.finished_at)}
        </Typography>
        {durationSecs != null && durationSecs >= 0 && (
          <Typography variant="caption" color="textSecondary" display="block">
            Duration: {formatDuration(durationSecs)}
          </Typography>
        )}
      </Box>
    );
  }

  if (isQueued && job.estimated_pending_seconds != null) {
    return (
      <Box>
        <Typography variant="caption" color="textSecondary" display="block">
          Estimated waiting
        </Typography>
        <Typography variant="body2">
          {formatDuration(job.estimated_pending_seconds)}
        </Typography>
      </Box>
    );
  }

  if (isRunning && job.started_at && job.resources?.walltime_seconds) {
    const elapsed = (now - new Date(job.started_at).getTime()) / 1000;
    const total = job.resources.walltime_seconds;
    const progress = Math.min(100, Math.max(0, (elapsed / total) * 100));
    const remaining = Math.max(0, total - elapsed);
    return (
      <Box sx={{ minWidth: 140 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
            Evicted in
          </Typography>
          <Typography
            sx={{ fontSize: FONT.xs, fontWeight: 600, color: INK_SECONDARY }}
          >
            {formatDuration(remaining)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 999,
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              backgroundColor: ACCENT,
            },
          }}
        />
      </Box>
    );
  }

  return <>-</>;
};
ProgressCell.propTypes = {
  job: PropTypes.object.isRequired,
};

export function sortJobs(jobs, sortBy = "submitted_at", sortDir = "desc") {
  return [...jobs].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "job_name") {
      cmp = (a.job_name || "").localeCompare(b.job_name || "");
    } else if (sortBy === "status") {
      cmp = (a.status || "").localeCompare(b.status || "");
    } else {
      const ta = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
      const tb = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
      cmp = ta - tb;
    }
    return (sortDir === "asc" ? cmp : -cmp) || a.job_id.localeCompare(b.job_id);
  });
}

export default function BatchJobListTable({
  jobs = [],
  loading = false,
  expandedRows = {},
  onToggleExpand,
  onActionMenuOpen,
  onJobPodActionMenuOpen,
  onViewJobLogs,
  onVisibleJobsChange,
  selectedJobs = new Map(),
  onSelectJobs,
  filterKey = "",
  selectionControls,
  selectionDisabled = false,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState("submitted_at");
  const [sortDir, setSortDir] = useState("desc");
  useEffect(() => setPage(0), [filterKey]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Math.min(10, parseInt(event.target.value, 10)));
    setPage(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  const { processedJobs, pageJobs } = useMemo(() => {
    const sorted = sortJobs(jobs, sortBy, sortDir);

    const lastPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
    const start = Math.min(page, lastPage) * rowsPerPage;
    const pageSlice = sorted.slice(start, start + rowsPerPage);

    return { processedJobs: sorted, pageJobs: pageSlice };
  }, [jobs, sortBy, sortDir, page, rowsPerPage]);

  const visibleIdsKey = JSON.stringify(pageJobs.map((job) => job.job_id));
  const inventoryIdsKey = JSON.stringify(jobs.map((job) => job.job_id));
  const viewKey = JSON.stringify([
    page,
    rowsPerPage,
    filterKey,
    sortBy,
    sortDir,
  ]);
  const previousInventoryIdsKey = useRef(inventoryIdsKey);
  const previousViewKey = useRef(viewKey);
  useEffect(() => {
    // Keep reporting page changes during refreshes so queued requests target
    // the new page. Wait for the inventory before reporting an initial view.
    if (loading && jobs.length === 0) return;
    const viewChanged = previousViewKey.current !== viewKey;
    const inventoryChanged =
      previousInventoryIdsKey.current !== inventoryIdsKey;
    previousViewKey.current = viewKey;
    previousInventoryIdsKey.current = inventoryIdsKey;

    // Notify the parent component about the currently visible jobs.
    // refresh immediately when the view or inventory has changed.
    onVisibleJobsChange?.(
      JSON.parse(visibleIdsKey),
      viewChanged || inventoryChanged,
    );
  }, [
    visibleIdsKey,
    inventoryIdsKey,
    viewKey,
    loading,
    jobs.length,
    onVisibleJobsChange,
  ]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(processedJobs.length / rowsPerPage) - 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [processedJobs.length, rowsPerPage, page]);

  // Cached rows stay visible during polling. Only hide the body when a
  // visible job is awaiting its first status response, or the list is empty.
  const showInitialProgress =
    loading &&
    (jobs.length === 0 || pageJobs.some((job) => !job.initialStatusLoaded));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 2,
          pb: 2,
          gap: 2,
          flexWrap: "nowrap",
          whiteSpace: "nowrap",
        }}
      >
        {selectionControls}
        <TablePagination
          rowsPerPageOptions={[5, 10]}
          component="div"
          count={processedJobs.length}
          rowsPerPage={rowsPerPage}
          page={Math.min(
            page,
            Math.max(0, Math.ceil(processedJobs.length / rowsPerPage) - 1),
          )}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          sx={{
            flexShrink: 0,
            "& .MuiTablePagination-toolbar": { pl: 1 },
            "& .MuiTypography-root": { fontSize: "0.875rem" },
          }}
        />
      </Box>

      <TableContainer
        sx={{ width: "100%", overflowX: "visible", mb: 0, boxShadow: "none" }}
      >
        <Table
          sx={{
            width: "100%",
            tableLayout: "fixed",
            ...dataTableHeadSx,
          }}
          aria-label="batch jobs table"
          aria-busy={loading}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} padding="checkbox">
                <Checkbox
                  slotProps={{ input: { "aria-label": "Select this page" } }}
                  disabled={selectionDisabled || !pageJobs.length}
                  checked={
                    pageJobs.length > 0 &&
                    pageJobs.every((job) => selectedJobs.has(job.job_id))
                  }
                  indeterminate={
                    pageJobs.some((job) => selectedJobs.has(job.job_id)) &&
                    !pageJobs.every((job) => selectedJobs.has(job.job_id))
                  }
                  onChange={(e) => onSelectJobs?.(pageJobs, e.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ width: "4%" }} />
              <SortableHeadCell
                label="Job Name"
                width="24%"
                active={sortBy === "job_name"}
                direction={sortBy === "job_name" ? sortDir : "asc"}
                onClick={() => handleSort("job_name")}
              />
              <SortableHeadCell
                label="Status"
                width="10%"
                active={sortBy === "status"}
                direction={sortBy === "status" ? sortDir : "asc"}
                onClick={() => handleSort("status")}
              />
              <TableCell sx={{ width: "18%" }}>Resources</TableCell>
              <SortableHeadCell
                label="Submitted At"
                width="16%"
                active={sortBy === "submitted_at"}
                direction={sortBy === "submitted_at" ? sortDir : "asc"}
                onClick={() => handleSort("submitted_at")}
              />
              <TableCell sx={{ width: "18%" }}>Progress</TableCell>
              <TableCell align="center" sx={{ width: "10%" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {showInitialProgress || pageJobs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  sx={{ borderBottom: "none" }}
                >
                  <Box
                    role="status"
                    aria-label="Jobs list state"
                    sx={{
                      minHeight: 192,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: 72,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {showInitialProgress ? (
                        <CircularProgress size={28} aria-label="Loading jobs" />
                      ) : (
                        <>
                          <WorkOutlineIcon
                            sx={{ fontSize: 40, color: INK_MUTED }}
                          />
                          <Typography
                            sx={{
                              fontSize: FONT.md,
                              fontWeight: 600,
                              color: INK,
                            }}
                          >
                            No matching jobs
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: FONT.sm,
                        color: INK_MUTED,
                        textAlign: "center",
                      }}
                    >
                      {pageJobs.length === 0
                        ? "Reset filters or submit a job to see more results."
                        : "Loading job details."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              pageJobs.map((job) => {
                const isExpanded = !!expandedRows[job.job_id];
                const isTerminal = TERMINAL_STATUSES.includes(job.status);
                const hasPods = (job.pods || []).length > 0;
                return (
                  <Fragment key={job.job_id}>
                    <TableRow
                      selected={selectedJobs.has(job.job_id)}
                      sx={{ "& > *": { borderBottom: "unset" } }}
                      hover
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          slotProps={{
                            input: {
                              "aria-label": `Select ${job.job_name || job.job_id}`,
                            },
                          }}
                          checked={selectedJobs.has(job.job_id)}
                          disabled={selectionDisabled}
                          onChange={(e) =>
                            onSelectJobs?.([job], e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => onToggleExpand?.(job.job_id)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell component="th" scope="row">
                        <Typography
                          sx={{
                            fontSize: FONT.md,
                            fontWeight: 600,
                            color: INK,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {job.job_name || "N/A"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: FONT.xs,
                            color: INK_MUTED,
                            fontFamily: "monospace",
                          }}
                        >
                          {job.job_id}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <JobStatusChip status={job.status} />
                      </TableCell>

                      <TableCell>
                        <WorkerResourceChips
                          cpuCores={job.resources?.cpu_request_cores}
                          memGib={job.resources?.memory_request_gb}
                          gpuCount={job.resources?.gpu_count}
                          gpuProduct={job.resources?.gpu_product}
                          podCount={job.resources?.node_count}
                        />
                      </TableCell>

                      <TableCell>{formatTimestamp(job.submitted_at)}</TableCell>

                      <TableCell>
                        <ProgressCell job={job} />
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          aria-label="job actions"
                          size="small"
                          onClick={(event) =>
                            onActionMenuOpen?.(
                              event,
                              job.job_id,
                              job.job_name,
                              job.status,
                            )
                          }
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded section: Pod details */}
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={8}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              my: 1,
                              p: 2,
                              backgroundColor: SURFACE_MUTED,
                              border: `1px solid ${HAIRLINE}`,
                              borderRadius: 2,
                            }}
                          >
                            <Typography
                              component="div"
                              sx={{
                                mb: 1.5,
                                fontSize: FONT.sm,
                                fontWeight: 600,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                color: INK_SECONDARY,
                              }}
                            >
                              Pod Details
                            </Typography>
                            {hasPods ? (
                              <PodDetailsTable
                                pods={(job.pods || []).map((pod) => ({
                                  ...pod,
                                  sshCmd: Array.isArray(job.resources?.commands)
                                    ? job.resources.commands.join("\n")
                                    : "",
                                }))}
                                commandLabel="Commands"
                                onPodActionMenuOpen={(event, pod) =>
                                  onJobPodActionMenuOpen?.(
                                    event,
                                    pod,
                                    job.job_id,
                                  )
                                }
                              />
                            ) : isTerminal ? (
                              <Chip
                                label="View Final Logs"
                                clickable
                                color={
                                  job.status === "Failed" ? "error" : "primary"
                                }
                                onClick={() => onViewJobLogs?.(job.job_id)}
                                sx={{ mt: 0.5 }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                No pods yet.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

BatchJobListTable.propTypes = {
  selectedJobs: PropTypes.instanceOf(Map),
  onSelectJobs: PropTypes.func,
  filterKey: PropTypes.string,
  selectionControls: PropTypes.node,
  selectionDisabled: PropTypes.bool,
  jobs: PropTypes.array,
  loading: PropTypes.bool,
  expandedRows: PropTypes.object,
  onToggleExpand: PropTypes.func,
  onActionMenuOpen: PropTypes.func,
  onJobPodActionMenuOpen: PropTypes.func,
  onViewJobLogs: PropTypes.func,
  onVisibleJobsChange: PropTypes.func,
};
